import React from 'react';
import Col from 'react-bootstrap/Col';
import Collapse from 'react-bootstrap/Collapse';
import Container from 'react-bootstrap/Container';
import Dropdown from 'react-bootstrap/Dropdown';
import Nav from 'react-bootstrap/Nav';
import Navbar from 'react-bootstrap/Navbar';
import Row from 'react-bootstrap/Row';
import { useTouchableDropdown } from '../../hooks/useTouchableDropdown';
import { ImageModel, LinkModel } from '../../models/globals';
import { Responsive } from '../common/Responsive';
import { SearchCourses } from '../common/SearchCourses';
import { AccountAndSearch } from './AccountAndSearch';
import { ArrowToggle } from './ArrowToggle';
import { SubMenu } from './SubMenu';
import { RtcBanner } from './RtcBanner';
import { publishOptimizelyExperimentDataEvent } from '../../../src/services/analytics';
import { getDocumentOrShadowRoot } from '../../utilities/DomUtilities';
import { headerAccessibility } from '../../web-accessibility/headerAccessibility';
import AnnouncementBanner from './AnnouncementBanner';

interface HeaderProps {
    model: HeaderModel;
    wholesalePriceModalCurrencyCode?: string | undefined;
}

interface HeaderModel {
    categoryMenuModels: CategoryMenuModel[];
    ed2GoResourceLinks: LinkModel[];
    accountLinks: LinkModel[];
    studentLoginLink: LinkModel;
    showStudentLoginLink: boolean;
    userDisplayName?: string | null;
    logo: ImageModel;
    rtcBannerCartIcon: ImageModel;
    rtcBannerArrowIcon: ImageModel;
    rtcBannerCloseIcon: ImageModel;
    homeLink: LinkModel;
    showEnvironmentTag: boolean;
    environmentName: string;
    searchLink: LinkModel;
    caaLink: LinkModel;
    autocompleteApiPath: string;
    searchPath: string;
    shoppingCartCount: number;
    shoppingCartLink: LinkModel;
    scopedCssUrl: string;
    typeAheadCssUrl: string;
    newContactPhoneNumber: string;
    newContactPhoneNumberUrl: string;
    wholesalePriceModalCurrencyCode?: string | undefined;
    cengageGroupLink: LinkModel | null;
    isCengageEd2GoLogoFlagEnabled: boolean;
    disableRTCBanner: boolean;
}

interface CategoryMenuModel {
    category: LinkModel;
    subCategories: LinkModel[];
}

const Header: React.FC<HeaderProps> = ({
    model: {
        categoryMenuModels,
        ed2GoResourceLinks,
        accountLinks,
        studentLoginLink,
        showStudentLoginLink,
        userDisplayName,
        logo,
        rtcBannerCartIcon,
        rtcBannerArrowIcon,
        rtcBannerCloseIcon,
        homeLink,
        showEnvironmentTag,
        environmentName,
        searchLink,
        autocompleteApiPath,
        searchPath,
        shoppingCartCount,
        shoppingCartLink,
        newContactPhoneNumber,
        newContactPhoneNumberUrl,
        wholesalePriceModalCurrencyCode,
        cengageGroupLink,
        isCengageEd2GoLogoFlagEnabled,
        disableRTCBanner,
    },
}) => {
    const [showSearch, setShowSearch] = React.useState(false);
    const [showTabletMenu, setShowTabletMenu] = React.useState(false);
    const { dropdown: coursesDropdown } = useTouchableDropdown();
    const { dropdown: resourcesDropdown } = useTouchableDropdown();
    const { dropdown: tabletCoursesDropdown } = useTouchableDropdown();
    const { dropdown: tabletResourcesDropdown } = useTouchableDropdown();

    const toggleShowSearch = () =>
        setShowSearch((prev) => {
            setTimeout(() => headerAccessibility.getSearchToggleFocusableElement(!prev), 0);
            return !prev;
        });
    const toggleShowTabletMenu = () => setShowTabletMenu((prev) => !prev);

    React.useEffect(() => {
        publishOptimizelyExperimentDataEvent(wholesalePriceModalCurrencyCode);
    }, []);

    React.useEffect(() => {
        if (!showSearch) return;

        const root = getDocumentOrShadowRoot('portal-header');
        if (!root) return;

        const handleKeyDown = (e: Event) => {
            const keyboardEvent = e as KeyboardEvent;
            if (keyboardEvent.key !== 'Tab') return;

            const path = e.composedPath?.();
            const target = path?.[0] instanceof HTMLElement ? path[0] : null;
            if (!target) return;

            const els = headerAccessibility.getHeaderElements();
            const targetType = headerAccessibility.getTargetType(target);

            if (keyboardEvent.shiftKey) {
                headerAccessibility.handleReverseTab(keyboardEvent, targetType, els);
            } else {
                headerAccessibility.handleForwardTab(keyboardEvent, targetType, els);
            }
        };

        root.addEventListener('keydown', handleKeyDown);
        return () => root.removeEventListener('keydown', handleKeyDown);
    }, [showSearch]);

    return (
        <>
            <header>
                {showEnvironmentTag && (
                    <div className="environment bg-info rounded p-0 font-weight-bold">{environmentName}</div>
                )}

                {!isCengageEd2GoLogoFlagEnabled && (
                    <AnnouncementBanner cengageLogo={logo} cengageGroupLink={cengageGroupLink!} />
                )}

                {!disableRTCBanner && (
                    <RtcBanner
                        rtcBannerCartIcon={rtcBannerCartIcon}
                        rtcBannerArrowIcon={rtcBannerArrowIcon}
                        rtcBannerCloseIcon={rtcBannerCloseIcon}
                        shoppingCartCount={shoppingCartCount}
                        shoppingCartLink={shoppingCartLink}
                    />
                )}

                <Navbar aria-label="header" className="border-bottom shadow-sm" expand="md">
                    <Container>
                        <Navbar.Toggle aria-controls="responsive-navbar-nav" className="mr-3 mobile-nav-toggle" />
                        <button
                            aria-controls="tablet-navbar-menu"
                            aria-expanded={showTabletMenu}
                            aria-label="Toggle tablet navigation"
                            className="navbar-toggler mr-3 tablet-nav-toggle"
                            data-testid="tablet-menu-toggle-button"
                            onClick={toggleShowTabletMenu}
                            type="button"
                        >
                            <span className="navbar-toggler-icon" />
                        </button>

                        <Navbar.Brand className="mr-auto nav-logo" href={homeLink.url}>
                            <img alt={logo.altText} className="w-100" src={logo.url} />
                        </Navbar.Brand>

                        <Responsive
                            className="mobile-account-and-search row"
                            testIdSuffix="account-and-search"
                            viewMode="mobile"
                        >
                            <AccountAndSearch
                                accountLinks={accountLinks}
                                showSearch={showSearch}
                                showStudentLoginLink={showStudentLoginLink}
                                userDisplayName={userDisplayName}
                                studentLoginLink={studentLoginLink}
                                toggleShowSearch={toggleShowSearch}
                                shoppingCartCount={shoppingCartCount}
                                shoppingCartLink={shoppingCartLink}
                            />
                        </Responsive>

                        <Navbar.Collapse className="header-main-nav" id="responsive-navbar-nav">
                            <Nav className="mr-auto zindex-header-menu">
                                <Dropdown
                                    {...coursesDropdown}
                                    as={Nav.Item}
                                    className="pl-3 navitem-border-bottom"
                                    focusFirstItemOnShow="keyboard"
                                    role="toolbar" // tells NVDA to enable navigation by arrow keys
                                >
                                    <Dropdown.Toggle as={ArrowToggle} id="courses-nav-dropdown">
                                        Courses
                                    </Dropdown.Toggle>

                                    <Dropdown.Menu renderOnMount>
                                        <Dropdown.Item active={false} href={searchLink.url}>
                                            <span className="menu-border-bottom">{searchLink.name}</span>
                                        </Dropdown.Item>

                                        <Dropdown.Divider />

                                        <Dropdown.ItemText>
                                            {categoryMenuModels.map(({ category, subCategories }) => (
                                                <SubMenu
                                                    category={category}
                                                    subCategories={subCategories}
                                                    key={`${category.name}-submenu`}
                                                />
                                            ))}
                                        </Dropdown.ItemText>
                                    </Dropdown.Menu>
                                </Dropdown>

                                <Dropdown
                                    {...resourcesDropdown}
                                    as={Nav.Item}
                                    className="pl-3 navitem-border-bottom"
                                    focusFirstItemOnShow="keyboard"
                                >
                                    <Dropdown.Toggle as={ArrowToggle} id="resources-nav-dropdown">
                                        Resources
                                    </Dropdown.Toggle>

                                    <Dropdown.Menu renderOnMount>
                                        {ed2GoResourceLinks.map(({ name, url }) => (
                                            <Dropdown.Item active={false} href={url} key={name}>
                                                <span className="menu-border-bottom">{name}</span>
                                            </Dropdown.Item>
                                        ))}
                                    </Dropdown.Menu>
                                </Dropdown>
                            </Nav>
                        </Navbar.Collapse>

                        <Responsive className="row" testIdSuffix="account-and-search" viewMode="desktop-and-tablet">
                            <AccountAndSearch
                                accountLinks={accountLinks}
                                showSearch={showSearch}
                                showStudentLoginLink={showStudentLoginLink}
                                userDisplayName={userDisplayName}
                                studentLoginLink={studentLoginLink}
                                toggleShowSearch={toggleShowSearch}
                                shoppingCartCount={shoppingCartCount}
                                shoppingCartLink={shoppingCartLink}
                            />
                        </Responsive>

                        <Nav className="border-left contact-nav d-none d-md-block ml-3 pl-2 text-center">
                            <Navbar.Text>
                                <div>CONTACT US</div>
                                <Nav.Link className="py-0" href={newContactPhoneNumberUrl}>
                                    {newContactPhoneNumber}
                                </Nav.Link>
                            </Navbar.Text>
                        </Nav>
                    </Container>
                </Navbar>

                <Collapse in={showTabletMenu} mountOnEnter unmountOnExit>
                    <div
                        className="border-bottom shadow-sm tablet-nav-menu"
                        data-testid="tablet-navbar-menu"
                        id="tablet-navbar-menu"
                    >
                        <Container>
                            <Nav className="py-2">
                                <Dropdown
                                    {...tabletCoursesDropdown}
                                    as={Nav.Item}
                                    className="navitem-border-bottom"
                                    focusFirstItemOnShow="keyboard"
                                    role="toolbar"
                                >
                                    <Dropdown.Toggle as={ArrowToggle} id="tablet-courses-nav-dropdown">
                                        Courses
                                    </Dropdown.Toggle>

                                    <Dropdown.Menu renderOnMount>
                                        <Dropdown.Item active={false} href={searchLink.url}>
                                            <span className="menu-border-bottom">{searchLink.name}</span>
                                        </Dropdown.Item>

                                        <Dropdown.Divider />

                                        <Dropdown.ItemText>
                                            {categoryMenuModels.map(({ category, subCategories }) => (
                                                <SubMenu
                                                    category={category}
                                                    subCategories={subCategories}
                                                    key={`${category.name}-tablet-submenu`}
                                                />
                                            ))}
                                        </Dropdown.ItemText>
                                    </Dropdown.Menu>
                                </Dropdown>

                                <Dropdown
                                    {...tabletResourcesDropdown}
                                    as={Nav.Item}
                                    className="navitem-border-bottom"
                                    focusFirstItemOnShow="keyboard"
                                >
                                    <Dropdown.Toggle as={ArrowToggle} id="tablet-resources-nav-dropdown">
                                        Resources
                                    </Dropdown.Toggle>

                                    <Dropdown.Menu renderOnMount>
                                        {ed2GoResourceLinks.map(({ name, url }) => (
                                            <Dropdown.Item active={false} href={url} key={`${name}-tablet`}>
                                                <span className="menu-border-bottom">{name}</span>
                                            </Dropdown.Item>
                                        ))}
                                    </Dropdown.Menu>
                                </Dropdown>
                            </Nav>
                        </Container>
                    </div>
                </Collapse>
            </header>

            <Collapse in={showSearch}>
                <div className="border-bottom shadow-sm">
                    <Container>
                        <Row className="justify-content-center py-3">
                            <Col md="8" lg="6">
                                <SearchCourses
                                    autocompleteApiPath={autocompleteApiPath}
                                    className="py-3"
                                    id="header-search-courses"
                                    placeholder="Search courses"
                                    searchPath={searchPath}
                                    isHeaderSearch={true}
                                    wholesalePriceModalCurrencyCode={wholesalePriceModalCurrencyCode}
                                />
                            </Col>
                        </Row>
                    </Container>
                </div>
            </Collapse>
        </>
    );
};

export { Header, HeaderProps, HeaderModel };
